/**
 * Refresh button for list pages.
 *
 * Every list already has its own fetch function; this just gives them all the
 * same control instead of each page inventing one. Follows the pattern already
 * used in MOProductWages: `mdi-refresh` with `animate-spin` while busy
 * (`.animate-spin` is defined in App.scss).
 *
 * `onClick` may return a promise. If it does, the button spins and stays
 * disabled until it settles — so a slow refresh can't be double-fired, and a
 * failed one still stops spinning.
 */
import { useState } from "react";

export default function RefreshButton({
  onClick,
  loading,
  disabled = false,
  title = "Refresh",
  className = "btn btn-sm btn-outline-primary",
}) {
  const [busy, setBusy] = useState(false);

  // The caller may drive the spinner from its own loading state instead.
  const spinning = loading !== undefined ? loading : busy;

  const handleClick = async () => {
    if (!onClick) return;
    try {
      setBusy(true);
      await onClick();
    } catch (err) {
      // Caught rather than re-thrown: letting it propagate out of an async
      // event handler produces an unhandled promise rejection in the console
      // on every failed refresh. Every caller's fetch already surfaces its own
      // errors to the user, so this only needs to not disappear silently for
      // whoever is debugging.
      console.error("Refresh failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled || spinning}
      title={title}
      aria-label={title}
    >
      <span className="d-flex align-items-center justify-content-center">
        <i className={`mdi mdi-refresh ${spinning ? "animate-spin" : ""}`}></i>
      </span>
    </button>
  );
}
